import discountQueries from '@apis/queries/discountQueries';
import Column from '@components/Column';
import styled from '@emotion/styled';
import usePriceUnit from '@hooks/item/usePriceUnit';
import useProject from '@hooks/useProject';
import { useFlow } from '@stackflow/react';
import useCalculatedRequest from '@store/order/hooks/useCalculatedRequest';
import { useQuery } from '@tanstack/react-query';
import { filterOrderCoupons, isItemCouponApplicableLine } from '@utils/couponUtils';
import { useTranslation } from 'react-i18next';

import SectionTitle from '../common/SectionTitle';
import SelectRow from '../common/SelectRow';

/** 쿠폰 및 선물 */
function OrderDiscounts() {
    const { t } = useTranslation('order');
    const priceUnit = usePriceUnit();
    const { push } = useFlow();
    const project = useProject();
    const giftEnabled = project?.features.giftEnabled;
    const {
        shopId, orderLines, totalAmount, discounts, giftAmount,
    } = useCalculatedRequest();
    const { data } = useQuery({
        ...discountQueries.list(shopId),
        enabled: !!shopId,
    });

    // 사용 가능한 쿠폰 수 = 장바구니(주문) 쿠폰 + 적용 가능한 상품이 있는 상품 쿠폰
    const orderCouponCount = totalAmount > 0
        ? filterOrderCoupons(data?.coupons, orderLines, totalAmount).length : 0;
    const itemCouponCount = data?.coupons
        .filter((coupon) => orderLines.some((line) => isItemCouponApplicableLine(coupon, line)))
        .length || 0;
    const couponCount = orderCouponCount + itemCouponCount;
    const couponAmount = discounts
        ?.filter((d) => d.type === 'COUPON')
        .reduce((acc, cur) => acc + cur.discountAmount, 0) || 0;
    const giftCount = data?.gifts.length || 0;
    const formatApplied = (amount: number) => t('discount.appliedAmount', {
        amount: `${amount.toLocaleString()}${priceUnit}`,
    });

    return (
        <Container fullWidth spacing={4}>
            <SectionTitle>
                {t(giftEnabled ? 'discount.sectionTitle' : 'discount.sectionTitleCouponOnly')}
            </SectionTitle>
            <Column fullWidth spacing={2}>
                <SelectRow label={t('discount.coupon')}
                    rightText={couponCount > 0 ? t('discount.owned', { count: couponCount }) : t('discount.none')}
                    appliedText={couponAmount > 0 ? formatApplied(couponAmount) : undefined}
                    disabled={couponCount === 0 && couponAmount === 0}
                    onClick={() => push('OrderCouponActivity', {})} />
                {giftEnabled && (
                    <SelectRow label={t('discount.gift')}
                        rightText={giftCount > 0 ? t('discount.owned', { count: giftCount }) : t('discount.none')}
                        appliedText={giftAmount > 0 ? formatApplied(giftAmount) : undefined}
                        disabled={giftCount === 0}
                        onClick={() => push('OrderGiftBottomSheet', {})} />
                )}
            </Column>
        </Container>
    );
}

const Container = styled(Column)`
    padding: var(--spacing-6) var(--spacing-4);
`;

export default OrderDiscounts;
